import { useMemo } from 'react'
import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'
import { BarChart } from '@mui/x-charts/BarChart'
import { useTranslation } from 'react-i18next'
import { formatMoney, formatKg } from '../shared/money'
import type { RevenueSeries } from '../shared/types'

/** Which quantity the bars plot. Revenue when the shop prices; kg or order count when it's priceless (D-069). */
export type ChartMetric = 'revenue' | 'orders' | 'weight'

/**
 * Formats an ISO bucket key into an axis label in the app's `12-Aug-2026` style, minus the part
 * the axis doesn't need — `29-Jun` per day, `Jun-2026` per month. The full form is too wide to
 * read on a 30-bar mobile axis. ponytail: two `toLocaleDateString` calls, not a date library.
 */
function formatLabel(bucket: string, granularity: string): string {
  const [y, m, d] = bucket.split('-').map(Number)
  const opts: Intl.DateTimeFormatOptions =
    granularity === 'MONTH' ? { month: 'short', year: 'numeric' } : { day: '2-digit', month: 'short' }
  return new Date(y, m - 1, granularity === 'MONTH' ? 1 : d)
    .toLocaleDateString('en-GB', opts)
    .replace(/\s+/g, '-')
}

/**
 * The over-time bar chart. Bars are the selected `metric` per time bucket (already zero-filled by
 * the server). Money is display-only; the server is authoritative. The x-axis auto-thins crowded labels.
 */
export default function RevenueChart({ series, metric }: { series: RevenueSeries; metric: ChartMetric }) {
  const { t } = useTranslation()
  const theme = useTheme()
  const labels = useMemo(
    () => series.points.map((p) => formatLabel(p.bucket, series.granularity)),
    [series],
  )
  const values = useMemo(
    () => series.points.map((p) => (metric === 'revenue' ? p.revenue : metric === 'weight' ? p.weight : p.orders)),
    [series, metric],
  )

  const label =
    metric === 'revenue'
      ? t('owner.dashboard.revenueTrend')
      : metric === 'weight'
        ? t('owner.dashboard.weightTrend')
        : t('owner.dashboard.ordersTrend')
  const format = (v: number) =>
    metric === 'revenue' ? formatMoney(v, series.currency) : metric === 'weight' ? formatKg(v) : String(Math.round(v))

  if (!values.some((v) => v > 0)) {
    return (
      <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
        {t('owner.dashboard.noData')}
      </Box>
    )
  }

  return (
    <BarChart
      height={300}
      xAxis={[{ scaleType: 'band', data: labels, tickLabelStyle: { fontSize: 10 } }]}
      series={[
        {
          data: values,
          label,
          color: theme.palette.secondary.main,
          valueFormatter: (v) => format(v ?? 0),
        },
      ]}
      slotProps={{ legend: { hidden: true } }}
      margin={{ top: 16, right: 16, bottom: 48, left: 64 }}
    />
  )
}
